import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../../main/initializers/axios';
import { ApiConstants } from '../../../main/constants/ApiConstants';
import '../Reservations/Reservations.css';

export interface ReservationUserOption {
  id: number;
  userId?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  documentType?: string | null;
  documentNumber?: string | null;
  birthDate?: string | null;
  nationality?: string | null;
  address?: string | null;
  notes?: string | null;
}

interface Props {
  onClose: () => void;
  onSelect: (profile: ReservationUserOption) => void;
}

const emptyProfileForm = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  documentType: '',
  documentNumber: '',
  birthDate: '',
  nationality: '',
  address: '',
  notes: '',
};

const GuestPickerModal = ({ onClose, onSelect }: Props) => {
  const { i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const [profiles, setProfiles] = useState<ReservationUserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(emptyProfileForm);

  useEffect(() => {
    const loadProfiles = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(ApiConstants.bookingRequest.bookingRequestProfileList, {
          params: { search: search.trim() },
        });
        setProfiles(Array.isArray(res.data) ? res.data : []);
      } catch {
        toast.error(i18n.t('reservationDesk.failedToLoadGuests'));
      } finally {
        setLoading(false);
      }
    };

    loadProfiles();
  }, [i18n, search]);

  const handleFormChange = (field: keyof typeof emptyProfileForm, value: string) => {
    setFormData((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleCreateProfile = async () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast.warning(i18n.t('reservationDesk.fillGuestRequiredFields'));
      return;
    }

    setSaving(true);
    try {
      const payload = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim() || null,
        email: formData.email.trim() || null,
        documentType: formData.documentType.trim() || null,
        documentNumber: formData.documentNumber.trim() || null,
        birthDate: formData.birthDate || null,
        nationality: formData.nationality.trim() || null,
        address: formData.address.trim() || null,
        notes: formData.notes.trim() || null,
      };

      const res = await axiosInstance.post(ApiConstants.bookingRequest.bookingRequestCreateProfile, payload);
      const createdProfile = res.data as ReservationUserOption;
      toast.success(i18n.t('reservationDesk.guestCreated'));
      onSelect(createdProfile);
    } catch {
      toast.error(i18n.t('reservationDesk.failedToCreateGuest'));
    } finally {
      setSaving(false);
    }
  };

  const formatProfileName = (profile: ReservationUserOption) => `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim() || `#${profile.id}`;

  return (
    <div className="reservation-modal guest-picker-overlay">
      <div className="reservation-modal-content guest-picker-modal">
        <div className="reservation-modal-top">
          <span className="reservation-kicker">Reservation user</span>
          <h3>{i18n.t('reservationDesk.guestPickerTitle')}</h3>
          <p>Select a linked user profile or create one from contact details.</p>
        </div>

        <div className="guest-picker-layout">
          <div className="guest-picker-panel">
            <div className="modal-field">
              <label>{i18n.t('generic.search')}</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={i18n.t('reservationDesk.searchGuestPlaceholder')}
              />
            </div>

            <div className="guest-picker-list">
              {loading && <div className="hotel-empty">{i18n.t('generic.search')}...</div>}

              {!loading && profiles.length === 0 && <div className="hotel-empty">{i18n.t('reservationDesk.noGuestsFound')}</div>}

              {!loading &&
                profiles.map((profile) => (
                  <button key={profile.id} type="button" className="guest-picker-item" onClick={() => onSelect(profile)}>
                    <strong>{formatProfileName(profile)}</strong>
                    <span>Profile #{profile.id}</span>
                    {profile.userId ? <span>Linked user #{profile.userId}</span> : <span>Standalone profile</span>}
                    {profile.phone && <span>{profile.phone}</span>}
                    {profile.email && <span>{profile.email}</span>}
                    {profile.documentNumber && <span>{profile.documentNumber}</span>}
                  </button>
                ))}
            </div>
          </div>

          <div className="guest-picker-panel">
            <div className="guest-picker-form-grid">
              <div className="modal-field">
                <label>{i18n.t('generic.firstName')}</label>
                <input type="text" value={formData.firstName} onChange={(e) => handleFormChange('firstName', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('generic.lastName')}</label>
                <input type="text" value={formData.lastName} onChange={(e) => handleFormChange('lastName', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('generic.phone')}</label>
                <input type="text" value={formData.phone} onChange={(e) => handleFormChange('phone', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('generic.email')}</label>
                <input type="email" value={formData.email} onChange={(e) => handleFormChange('email', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('reservationDesk.documentType')}</label>
                <input type="text" value={formData.documentType} onChange={(e) => handleFormChange('documentType', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('reservationDesk.documentNumber')}</label>
                <input type="text" value={formData.documentNumber} onChange={(e) => handleFormChange('documentNumber', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('reservationDesk.birthDate')}</label>
                <input type="date" value={formData.birthDate} onChange={(e) => handleFormChange('birthDate', e.target.value)} />
              </div>

              <div className="modal-field">
                <label>{i18n.t('reservationDesk.nationality')}</label>
                <input type="text" value={formData.nationality} onChange={(e) => handleFormChange('nationality', e.target.value)} />
              </div>

              <div className="modal-field guest-picker-wide">
                <label>{i18n.t('generic.address')}</label>
                <input type="text" value={formData.address} onChange={(e) => handleFormChange('address', e.target.value)} />
              </div>

              <div className="modal-field guest-picker-wide">
                <label>{i18n.t('generic.notes')}</label>
                <input type="text" value={formData.notes} onChange={(e) => handleFormChange('notes', e.target.value)} />
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" onClick={handleCreateProfile} disabled={saving}>
                Create profile
              </button>
              <button type="button" onClick={onClose}>
                {i18n.t('generic.cancel')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuestPickerModal;
